import { NodeKind, TransformKind } from '@/core/document'
import { CROP_TOOL } from '@/tools/crop'
import { ADJUSTMENTS_TOOL } from '@/tools/adjustments'
import { COLLAGE_TOOL } from '@/tools/collage'
import { EXPORT_TOOL, FORMAT_TOOL } from '@/tools/output'
import { FRAME_TOOL } from '@/tools/frame'
import { PREVIEW_TOOL } from '@/tools/preview'
import { QUALITY_TOOL } from '@/tools/quality'
import {
  ToolKind,
  ToolTab,
  UtilityId,
  type NodeTool,
  type Tool,
  type TransformTool,
  type UtilityTool,
} from '@/tools/types'

export const TOOLS = [CROP_TOOL, ADJUSTMENTS_TOOL, FRAME_TOOL, COLLAGE_TOOL, FORMAT_TOOL, EXPORT_TOOL] as const satisfies readonly Tool[]
export const UTILITIES = [PREVIEW_TOOL, QUALITY_TOOL] as const satisfies readonly UtilityTool[]

function isTransformTool(tool: Tool): tool is TransformTool {
  return tool.kind === ToolKind.Transform
}

function isTransformKind(value: string): value is TransformKind {
  return Object.values(TransformKind).some((kind) => kind === value)
}

export function validateToolList(tools: readonly Tool[]): void {
  const seenTransforms = new Set<TransformKind>()
  const seenNodes = new Set<NodeKind>()
  const seenTabs = new Set<ToolTab>()

  for (const tool of tools) {
    if (!Object.values(ToolTab).includes(tool.tab)) {
      throw new Error(`Tool "${tool.label}" has unknown tab "${String(tool.tab)}"`)
    }
    if (seenTabs.has(tool.tab)) throw new Error(`Tool registry has duplicate tab "${tool.tab}"`)
    seenTabs.add(tool.tab)
    if (!tool.label.trim()) throw new Error(`Tool kind "${tool.kind}" must have a label`)
    if (!tool.icon) throw new Error(`Tool "${tool.label}" is missing its icon component`)
    if (!tool.Panel) throw new Error(`Tool "${tool.label}" is missing its Panel component`)
    if (typeof tool.appliesTo !== 'function') throw new Error(`Tool "${tool.label}" is missing appliesTo`)

    switch (tool.kind) {
      case ToolKind.Transform:
        if (tool.transforms.length === 0) throw new Error(`Transform tool "${tool.label}" owns no transforms`)
        for (const transform of tool.transforms) {
          if (!Object.values(TransformKind).includes(transform)) {
            throw new Error(`Tool registry has unknown transform "${String(transform)}"`)
          }
          if (seenTransforms.has(transform)) {
            throw new Error(`Tool registry has duplicate transform "${transform}"`)
          }
          seenTransforms.add(transform)
          const definition = tool.definitions[transform]
          if (!definition || typeof definition.createDefault !== 'function') {
            throw new Error(`Transform tool "${transform}" is missing createDefault`)
          }
          if (typeof definition.summarize !== 'function') {
            throw new Error(`Transform tool "${transform}" is missing summarize`)
          }
        }
        for (const configuredTransform of Object.keys(tool.definitions)) {
          if (!isTransformKind(configuredTransform)) {
            throw new Error(`Tool "${tool.label}" configures unknown transform "${configuredTransform}"`)
          }
          if (!tool.transforms.includes(configuredTransform)) {
            throw new Error(`Tool "${tool.label}" configures unowned transform "${configuredTransform}"`)
          }
        }
        break
      case ToolKind.Node:
        if (tool.node === NodeKind.Image || !Object.values(NodeKind).includes(tool.node)) {
          throw new Error(`Tool registry has unsupported composition node "${String(tool.node)}"`)
        }
        if (seenNodes.has(tool.node)) throw new Error(`Tool registry has duplicate Node tool "${tool.node}"`)
        seenNodes.add(tool.node)
        break
      case ToolKind.Output:
        break
      default:
        throw new Error(`Tool registry has unknown tool kind "${String((tool as { kind: unknown }).kind)}"`)
    }
  }

  for (const transform of Object.values(TransformKind)) {
    if (!seenTransforms.has(transform)) {
      throw new Error(`Tool registry is missing Transform tool "${transform}"`)
    }
  }
  for (const node of Object.values(NodeKind)) {
    if (node !== NodeKind.Image && !seenNodes.has(node)) {
      throw new Error(`Tool registry is missing Node tool "${node}"`)
    }
  }
  for (const tab of Object.values(ToolTab)) {
    if (!seenTabs.has(tab)) throw new Error(`Tool registry is missing tab "${tab}"`)
  }
}

export function getNodeTool(node: NodeKind): NodeTool {
  const tool = TOOLS.find((candidate) => candidate.kind === ToolKind.Node && candidate.node === node)
  if (!tool || tool.kind !== ToolKind.Node) throw new Error(`Unknown Node tool: "${node}"`)
  return tool
}

export function validateToolRegistry(): void {
  validateToolList(TOOLS)
  validateUtilityList(UTILITIES)
}

export function validateUtilityList(utilities: readonly UtilityTool[]): void {
  const seenIds = new Set<UtilityId>()
  for (const utility of utilities) {
    if (utility.kind !== ToolKind.Utility) throw new Error(`Utility "${utility.label}" has invalid kind "${String(utility.kind)}"`)
    if (!Object.values(UtilityId).includes(utility.id)) throw new Error(`Utility registry has unknown id "${String(utility.id)}"`)
    if (seenIds.has(utility.id)) throw new Error(`Utility registry has duplicate id "${utility.id}"`)
    seenIds.add(utility.id)
    if (!utility.label.trim()) throw new Error(`Utility "${utility.id}" must have a label`)
    if (!utility.icon) throw new Error(`Utility "${utility.id}" is missing its icon component`)
    if (!utility.Overlay) throw new Error(`Utility "${utility.id}" is missing its Overlay component`)
  }
  for (const id of Object.values(UtilityId)) {
    if (!seenIds.has(id)) throw new Error(`Utility registry is missing id "${id}"`)
  }
}

export function getUtility(id: UtilityId): UtilityTool {
  const utility = UTILITIES.find((candidate) => candidate.id === id)
  if (!utility) throw new Error(`Unknown utility: "${id}"`)
  return utility
}

export function getTransformTool(transform: TransformKind): TransformTool {
  const tool = TOOLS.find((candidate) => candidate.kind === ToolKind.Transform && candidate.transforms.includes(transform))
  if (!tool || !isTransformTool(tool)) throw new Error(`Unknown transform tool: "${transform}"`)
  return tool
}

export function getTool(tab: ToolTab): Tool {
  const tool = TOOLS.find((candidate) => candidate.tab === tab)
  if (!tool) throw new Error(`Unknown tool tab: "${tab}"`)
  return tool
}
